
import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import { useProject } from "@/contexts/ProjectContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, AlertTriangle, Calendar, Clock, Edit, Save, Trash, User } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useIsMobile } from "@/hooks/use-mobile";

export default function RiskDetail() {
  const { riskId } = useParams();
  const navigate = useNavigate();
  const { currentProject, updateRisk } = useProject();
  const isMobile = useIsMobile();
  
  if (!currentProject) {
    navigate("/projects-selector");
    return null;
  }
  
  const risk = currentProject.risks.find(r => r.id === riskId);
  
  if (!risk) {
    return (
      <div className="flex flex-col min-h-screen">
        <NavBar />
        <main className="container py-6 flex-1 flex items-center justify-center">
          <div className="text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Risk Not Found</h1>
            <p className="text-muted-foreground mb-6">The risk you're looking for doesn't exist or has been deleted.</p>
            <Button onClick={() => navigate("/dashboard")}>Return to Dashboard</Button>
          </div>
        </main>
      </div>
    );
  }
  
  const riskScore = risk.probability * risk.impact;
  const getRiskScoreClass = () => {
    if (riskScore >= 16) return "bg-red-100 text-red-800";
    if (riskScore >= 10) return "bg-orange-100 text-orange-800";
    if (riskScore >= 4) return "bg-amber-100 text-amber-800";
    return "bg-green-100 text-green-800";
  };
  
  const getRiskSeverity = () => {
    if (riskScore >= 16) return "Critical";
    if (riskScore >= 10) return "High";
    if (riskScore >= 4) return "Medium";
    return "Low";
  };

  return (
    <div className="flex flex-col min-h-screen">
      <NavBar />
      <main className="container py-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate("/risks")} className="w-full sm:w-auto">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Risks
          </Button>
          <div className="flex-1">
            <h1 className="text-xl sm:text-3xl font-bold">{risk.name}</h1>
            <div className="flex items-center gap-2 text-muted-foreground">
              <span>ID: {risk.id}</span>
              <span className="hidden sm:inline-block">•</span>
              <span>Stage {risk.stage}</span>
            </div>
          </div>
          {!isMobile && (
            <div className="flex gap-2">
              <Button variant="outline">
                <Edit className="mr-2 h-4 w-4" /> Edit
              </Button>
              <Button variant="destructive">
                <Trash className="mr-2 h-4 w-4" /> Delete
              </Button>
            </div>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Risk Details</CardTitle>
                <CardDescription>Comprehensive information about this risk</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Description</Label>
                  <div className="p-3 bg-gray-50 rounded-md mt-1">
                    {risk.description}
                  </div>
                </div>
                
                <div>
                  <Label>Mitigation Strategy</Label>
                  <div className="p-3 bg-gray-50 rounded-md mt-1">
                    {risk.mitigation}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Current Status</Label>
                    <div className={`mt-1 p-2 rounded-md font-medium text-center capitalize ${
                      risk.status === 'open' ? 'bg-amber-100 text-amber-800' :
                      risk.status === 'mitigated' ? 'bg-blue-100 text-blue-800' : 
                      'bg-green-100 text-green-800'
                    }`}>
                      {risk.status}
                    </div>
                  </div>
                  
                  <div>
                    <Label>Risk Score</Label>
                    <div className={`mt-1 p-2 rounded-md font-medium text-center ${getRiskScoreClass()}`}>
                      {riskScore} - {getRiskSeverity()}
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Probability (1-5)</Label>
                    <div className="mt-1 p-2 bg-gray-50 rounded-md">
                      <div className="flex items-center">
                        <div className="flex-1 h-2 bg-gray-200 rounded-full">
                          <div 
                            className="h-2 bg-blue-500 rounded-full" 
                            style={{ width: `${risk.probability * 20}%` }}
                          />
                        </div>
                        <span className="ml-3 font-medium">{risk.probability}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Impact (1-5)</Label>
                    <div className="mt-1 p-2 bg-gray-50 rounded-md">
                      <div className="flex items-center">
                        <div className="flex-1 h-2 bg-gray-200 rounded-full">
                          <div 
                            className="h-2 bg-red-500 rounded-full" 
                            style={{ width: `${risk.impact * 20}%` }}
                          />
                        </div>
                        <span className="ml-3 font-medium">{risk.impact}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {isMobile && (
                  <div className="flex gap-2 mt-4">
                    <Button variant="outline" className="flex-1">
                      <Edit className="mr-2 h-4 w-4" /> Edit
                    </Button>
                    <Button variant="destructive" className="flex-1">
                      <Trash className="mr-2 h-4 w-4" /> Delete
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
            
            <div className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Risk Management History</CardTitle>
                  <CardDescription>Tracking of actions and updates</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="border-l-2 border-blue-500 pl-4 pb-4">
                      <p className="text-sm font-medium">Risk Identified</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(risk.dateIdentified).toLocaleDateString()} • {risk.owner}
                      </p>
                      <p className="mt-2 text-sm">Initial risk assessment completed and added to register.</p>
                    </div>
                    
                    <div className="border-l-2 border-amber-500 pl-4 pb-4">
                      <p className="text-sm font-medium">Mitigation Strategy Defined</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(risk.dateUpdated).toLocaleDateString()} • Project Manager
                      </p>
                      <p className="mt-2 text-sm">Mitigation approach defined and owner assigned.</p>
                    </div>
                    
                    {risk.status !== 'open' && (
                      <div className="border-l-2 border-green-500 pl-4 pb-4">
                        <p className="text-sm font-medium">Risk {risk.status.charAt(0).toUpperCase() + risk.status.slice(1)}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date().toLocaleDateString()} • {risk.owner}
                        </p>
                        <p className="mt-2 text-sm">
                          {risk.status === 'mitigated' 
                            ? 'Mitigation actions completed successfully.' 
                            : 'Risk has been closed and is no longer active.'}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {risk.status === 'open' && (
                    <div className="mt-6 pt-4 border-t">
                      <h4 className="font-medium mb-3">Update Risk Status</h4>
                      <div className="flex gap-3 flex-col sm:flex-row">
                        <Select defaultValue={risk.status}>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="mitigated">Mitigated</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button className="w-full sm:w-auto">
                          <Save className="mr-2 h-4 w-4" /> Save
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
          
          <div>
            <Card className="mb-6">
              <CardHeader className="pb-2">
                <CardTitle>Risk Owner</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                    <User className="h-5 w-5 text-gray-500" />
                  </div>
                  <div>
                    <p className="font-medium">{risk.owner}</p>
                    <p className="text-sm text-muted-foreground">Risk Owner</p>
                  </div>
                </div>
                <Button variant="outline" className="w-full">
                  <Edit className="mr-2 h-4 w-4" />
                  Change Owner
                </Button>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle>Key Dates</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">Identified</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <p className="font-medium">{new Date(risk.dateIdentified).toLocaleDateString()}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Last Updated</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <p className="font-medium">{new Date(risk.dateUpdated).toLocaleDateString()}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">RIBA Stage</p>
                  <p className="font-medium mt-1">Stage {risk.stage}: {currentProject.stages[risk.stage]?.name || "Unknown"}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
